import Stack from 'atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'atoms/Typography/Typography';
import { useStaticQuery, graphql } from 'gatsby';
import ContentAndImageCardsHorizontal from 'molecules/ContentAndImageCardsHorizontal/ContentAndImageCardsHorizontal';
import HeadingAndSupportingText from 'molecules/HeadingAndSupportingText/HeadingAndSupportingText';
import { ImageCardHorizontalProps } from 'molecules/ImageCardHorizontal/ImageCardHorizontal';
import ThumbnailWithPlayButton from 'molecules/ThumbnailWithPlayButton/ThumbnailWithPlayButton';
import React, { useState } from 'react';
import OverlayVideoPortal from 'src/app_previous/elements/video/OverlayVideoPortal';
import EmailModal from 'src/app_previous/organisms/Standard/ProfitSales/2024_Q3_profit_sales_redesign/EmailModal/EmailModal';
import { usePeopleMagicProfitEmailModalControl } from 'src/hooks/usePeopleMagicProfitEmailModalControl';
import useMasterclassData from 'app/templates/MasterclassArticle/useMasterclassData';

const PeopleMagicVideos = (): JSX.Element => {
  const { thumbnail, card1, card2, card3 } = useStaticQuery(graphql`
    {
      thumbnail: contentfulAsset(contentful_id: { eq: "6RQ87aCOh5fShtyxJVlznR" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1450)
      }
      card1: contentfulAsset(contentful_id: { eq: "s2KE7UEAej07PVIFxw8f1" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 800)
      }
      card2: contentfulAsset(contentful_id: { eq: "OMP64mS1T1CLRpui2UWTC" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 800)
      }
      card3: contentfulAsset(contentful_id: { eq: "3R5CCmffe5dPmK2jEeA1Wf" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 800)
      }
    }
  `);

  const { open } = usePeopleMagicProfitEmailModalControl();

  const baseTitleProps: TypographyProps = {
    type: 'paragraph',
    size: 'lg',
    fontWeight: 'font-weight-semibold',
    textAlign: 'left',
  };

  const baseSupportingTextProps: TypographyProps = {
    type: 'paragraph',
    size: 'md',
    color: 'text-tertiary',
    textAlign: 'left',
  };

  const imageMaxWidth = 175;

  const onCardClick = open;

  const cards: ImageCardHorizontalProps[] = [
    {
      image: {
        image: card1.gatsbyImageData,
        alt: 'Card 1',
      },
      imageMaxWidth,
      onClick: onCardClick,
      title: {
        ...baseTitleProps,
        text: 'Clear community design',
      },
      supportingText: {
        ...baseSupportingTextProps,
        text: 'Create a community so well designed it sells itself.',
      },
    },
    {
      image: {
        image: card2.gatsbyImageData,
        alt: 'Card 2',
      },
      imageMaxWidth,
      onClick: onCardClick,
      title: {
        ...baseTitleProps,
        text: '9 essential principles',
      },
      supportingText: {
        ...baseSupportingTextProps,
        text: 'Harness the power of community to build the perfect business.',
      },
    },
    {
      image: {
        image: card3.gatsbyImageData,
        alt: 'Card 3',
      },
      imageMaxWidth,
      onClick: onCardClick,
      title: {
        ...baseTitleProps,
        text: 'A proven pricing formula',
      },
      supportingText: {
        ...baseSupportingTextProps,
        text: 'Monetize with confidence and set yourself up to succeed.',
      },
    },
  ];

  const renderContent = () => (
    <Stack
      direction='column'
      gap='spacing-3xl'
      justify='center'
      align='center'
    >
      <ThumbnailWithPlayButton
        imageProps={{
          image: thumbnail.gatsbyImageData,
          alt: 'People Magic Video Thumbnail',
        }}
        onClick={() => setVideoModalOpen(true)}
      />
      <Stack
        padding={['spacing-none', 'spacing-3xl']}
        paddingLg='spacing-none'
      >
        <HeadingAndSupportingText
          heading={{
            type: 'paragraph',
            size: 'lg',
            fontWeight: 'font-weight-semibold',
            textAlign: 'left',
            text: 'Niching down won’t get you where you need to be.',
          }}
          gap='spacing-xs'
          supportingText={{
            type: 'paragraph',
            size: 'md',
            color: 'text-tertiary',
            textAlign: 'left',
            text: 'You don’t need to crank out more content either. You need one person, going through a specific transition.',
          }}
        />
      </Stack>
    </Stack>
  );

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const videoWistiaId = useMasterclassData()[1].heroBannerData.wistiaVideoId;

  return (
    <Stack
      direction='column'
      align='center'
      justify='flex-start'
      gap='spacing-7xl'
      gapSm='spacing-4xl'
      padding={['spacing-none', 'spacing-4xl']}
      paddingSm={['spacing-none', 'spacing-xl']}
    >
      <Stack
        maxWidth='width-xl'
        justify='flex-start'
        align='flex-start'
        marginRight='spacing-auto'
      >
        <Typography
          type='heading'
          size='2xl'
          fontWeight='font-weight-semibold'
          textAlign='left'
        >
          Break through with proven strategies
        </Typography>
      </Stack>
      <ContentAndImageCardsHorizontal
        content={renderContent()}
        contentPosition='left'
        leftLabel='People magic starts with your ideal member.'
        rightLabel='We take you step by step from there 👇'
        cards={cards}
      />
      {/* TODO (MKT-9357): Migrate the BaseModal to the new app folder and then migrate the EmailModal to the new app folder */}
      <EmailModal />
      {/* TODO (MKT-9345): Migrate OverlayVideo and its related elements to the new app folder and architecture */}
      <OverlayVideoPortal
        overlayVisible={videoModalOpen}
        setOverlayVisible={setVideoModalOpen}
        wistiaId={videoWistiaId}
        videoLabel='People Magic Video'
      />
    </Stack>
  );
};

export default PeopleMagicVideos;
